/**
 * StartChatInboxPicker
 *
 * Popover/Dialog para iniciar uma conversa com um contato existente.
 *
 * - Lista somente as inboxes WhatsApp em que o usuário logado tem permissão
 *   (reaproveita a RPC `get_user_accessible_inboxes`, a mesma usada em
 *   `/tenant/chat` → `fetchInboxes()`).
 * - Ao selecionar uma inbox, cria/obtém a conversa via RPC
 *   `get_or_create_conversation` e devolve o `conversationId` para o caller,
 *   que abre o `ChatConversationModal` IN-PLACE (sem sair do kanban).
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.1.0 - Nova prop opcional `contentClassName` para permitir
 *            sobrepor o z-index quando o picker é aberto dentro de outro modal elevado
 *            (ex.: Compartilhar proposta usa a camada elevada z-[70]).
 *   RELEASE: 2026-xx-xx v1.0.0 - Versão inicial.
 *
 * Versão: 1.1.0
 */

import { useEffect, useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, Inbox as InboxIcon, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';
import { formatInboxIdentifierForDisplay } from '@/lib/phone-utils';

// Logger centralizado do componente
const logger = createLogger('StartChatInboxPicker');

// Canais que podem iniciar conversa (WhatsApp livre e Oficial)
const ALLOWED_CHANNELS = new Set(['whatsapp', 'whatsapp_oficial']);

// Shape mínimo retornado pela RPC `get_user_accessible_inboxes`
interface AccessibleInbox {
  id: string;
  name: string;
  channel_type: string;
  identifier: string;
  status: string;
  access_restricted: boolean;
}

// Props públicas do componente
interface StartChatInboxPickerProps {
  /** Controla a abertura */
  open: boolean;
  /** Callback de abertura/fechamento */
  onOpenChange: (open: boolean) => void;
  /** ID do contato (já existente) que vai receber a conversa */
  contactId: string;
  /** Nome do contato — apenas para UX */
  contactName?: string | null;
  /** Disparado quando a conversa é criada/obtida com sucesso */
  onConversationReady: (conversationId: string) => void;
  /**
   * Trigger opcional. Quando fornecido, o componente renderiza como Popover
   * ancorado nesse trigger. Quando ausente, renderiza como Dialog centralizado
   * (usado nos empty states das abas de chat).
   */
  trigger?: React.ReactNode;
  /**
   * Classes extras aplicadas ao DialogContent (modo Dialog). Útil para elevar o
   * z-index quando o picker é aberto a partir de outro modal (ex.: `z-[70]`).
   */
  contentClassName?: string;
}

/**
 * Componente reutilizável de seleção de inbox para iniciar conversa.
 */
export function StartChatInboxPicker({
  open,
  onOpenChange,
  contactId,
  contactName,
  onConversationReady,
  trigger,
  contentClassName,
}: StartChatInboxPickerProps) {
  const { user, currentTenant } = useAuth();

  const [inboxes, setInboxes] = useState<AccessibleInbox[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  // Busca inboxes acessíveis quando o picker abre
  useEffect(() => {
    if (!open) return;
    if (!user?.id || !currentTenant?.id) return;

    let cancelled = false;

    const fetchInboxes = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_user_accessible_inboxes', {
          _user_id: user.id,
          _tenant_id: currentTenant.id,
        });

        if (error) throw error;
        if (cancelled) return;

        const rows = (data as AccessibleInbox[] | null) || [];

        // Filtra apenas WhatsApp ativas (regra de iniciar conversa)
        const filtered = rows.filter(
          (i) => ALLOWED_CHANNELS.has(i.channel_type) && i.status === 'active',
        );

        setInboxes(filtered);
        logger.debug('Inboxes elegíveis carregadas', {
          total: rows.length,
          elegiveis: filtered.length,
        });
      } catch (err) {
        logger.error('Erro ao carregar inboxes acessíveis', err);
        if (!cancelled) {
          toast.error('Não foi possível carregar suas inboxes.');
          setInboxes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInboxes();
    return () => {
      cancelled = true;
    };
  }, [open, user?.id, currentTenant?.id]);

  // Seleciona uma inbox → cria/obtém conversa e devolve para o caller
  const handleSelectInbox = async (inboxId: string) => {
    if (!contactId) {
      toast.error('Contato inválido.');
      return;
    }
    if (creatingFor) return;

    setCreatingFor(inboxId);
    try {
      const { data: conversationId, error } = await supabase.rpc(
        'get_or_create_conversation',
        {
          _inbox_id: inboxId,
          _contact_id: contactId,
        },
      );

      if (error) throw error;
      if (!conversationId) {
        throw new Error('Conversa não retornada pela RPC.');
      }

      logger.debug('Conversa pronta', { conversationId, inboxId });

      onConversationReady(conversationId as string);
      onOpenChange(false);
    } catch (err) {
      logger.error('Erro ao iniciar conversa', err);
      toast.error('Não foi possível iniciar a conversa.');
    } finally {
      setCreatingFor(null);
    }
  };

  // Conteúdo compartilhado entre Popover e Dialog
  const content = (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium leading-none">Iniciar conversa</p>
        <p className="text-xs text-muted-foreground mt-1">
          {contactName
            ? `Escolha a inbox para falar com ${contactName}.`
            : 'Escolha a inbox para iniciar a conversa.'}
        </p>
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full rounded-md" />
          <Skeleton className="h-12 w-full rounded-md" />
        </div>
      ) : inboxes.length === 0 ? (
        <div className="rounded-md border border-dashed p-4 text-center">
          <InboxIcon className="h-6 w-6 mx-auto text-muted-foreground/60 mb-2" />
          <p className="text-xs text-muted-foreground">
            Você não tem acesso a nenhuma inbox WhatsApp ativa para iniciar
            conversas.
          </p>
        </div>
      ) : (
        <div className="space-y-1 max-h-[140px] overflow-y-auto -mx-1 px-1 pr-2">
          {inboxes.map((inbox) => {
            const isCreating = creatingFor === inbox.id;
            return (
              <button
                key={inbox.id}
                type="button"
                onClick={() => handleSelectInbox(inbox.id)}
                disabled={!!creatingFor}
                className={cn(
                  'w-full flex items-center gap-3 p-2.5 rounded-md border bg-card text-left transition-colors',
                  'hover:bg-muted/60 hover:border-primary/30',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                )}
              >
                <div className="h-9 w-9 rounded-md bg-green-500/10 ring-1 ring-green-500/20 flex items-center justify-center shrink-0">
                  <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{inbox.name}</p>
                  {inbox.identifier && (
                    <p className="text-[11px] text-muted-foreground truncate">
                      {formatInboxIdentifierForDisplay(
                        inbox.channel_type,
                        inbox.identifier,
                      )}
                    </p>
                  )}
                </div>
                {isCreating && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // Modo Popover (quando há trigger) — ideal para o botão do card
  if (trigger) {
    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[320px] p-3"
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  // Modo Dialog (fallback) — usado nos empty states das abas
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-[400px]', contentClassName)}>
        <DialogHeader>
          <DialogTitle className="text-base">Iniciar conversa</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
